import gsap from 'gsap';
import ScrollTrigger from 'gsap/ScrollTrigger';
import React, { useEffect, useRef } from 'react';

import MobileImage from './mobile-image.png';
import * as styles from './MobileApps.module.scss';
import Particles from './Particles';

gsap.registerPlugin(ScrollTrigger);
gsap.defaults({
  duration: 0.5,
});

export default function MobileApps({ containerRef }) {
  const trigger = useRef();
  const tl = useRef();

  useEffect(() => {
    // GSAP stuff
    const layers = ['#phone', '#filter', '#scan', '#scan-line', '#dom', '#crash-alert'];

    const ctx = gsap.context(() => {
      tl.current = gsap
        .timeline({
          scrollTrigger: {
            trigger: trigger.current,
            start: () => 'top center',
            end: () => '+=' + containerRef.current.clientHeight * 0.65,
            scrub: 4,
          },
        })
        .set(layers, { transformOrigin: 'center', transformStyle: 'preserve-3d' })
        .to(
          layers,
          {
            ease: 'none',
            overwrite: true,
            skewY: -25,
            scaleX: 0.75,
            xPercent: -40,
          },
          'turn'
        )
        .to(
          '#phone-faded',
          {
            opacity: 1,
          },
          'turn'
        )
        .to(
          ['#filter', '#dom'],
          {
            opacity: 1,
          },
          'expand'
        )
        .to(
          '#filter',
          {
            x: gsap.getProperty('#filter', '--x'),
          },
          'expand'
        )
        .to(
          '#scan',
          {
            x: gsap.getProperty('#scan', '--x'),
          },
          'expand'
        )
        .to(
          '#scan-line',
          {
            x: gsap.getProperty('#scan-line', '--x'),
          },
          'expand'
        )
        .to(
          '#dom',
          {
            x: gsap.getProperty('#dom', '--x'),
          },
          'expand'
        )
        .to(
          '#crash-alert',
          {
            x: gsap.getProperty('#crash-alert', '--x'),
            yPercent: -60,
          },
          'expand'
        )
        .to(
          '#particles',
          {
            opacity: 1,
          },
          'expand'
        )
        .to(
          '#scan-line',
          {
            opacity: 1,
            duration: 0.1,
          },
          'scan'
        )
        .to(
          '#scan-line',
          {
            duration: 1,
            y: '520px',
          },
          'scan'
        )
        .to(
          '#scan',
          {
            opacity: 1,
            duration: 1,
          },
          'scan'
        )
        .to(
          '#scan-text',
          {
            opacity: 1,
          },
          'scan'
        )
        .to('#scan-line', {
          opacity: 0,
          duration: 0.1,
        })
        .to(
          '#mobile-visual',
          {
            yPercent: 0,
          },
          '<'
        )
        .to(['#crash-alert', '#crash-text'], {
          opacity: 1,
        })
        .to(
          ['#scan-text', '#crash-alert', '#crash-text'],
          {
            opacity: 0,
          },
          '>+=1.5'
        )
        .to(
          '#privacy-text',
          {
            opacity: 1,
          },
          '>+=1.5'
        )
        .to('#rules', {
          opacity: 1,
        })
        .to(
          ['#privacy-text', '#rules', '#particles'],
          {
            opacity: 0,
          },
          '>+=5'
        )
        .to('#sdk-squares path', {
          opacity: 1,
          stagger: -0.25,
        })
        .to('#sdk-squares', {
          opacity: 0,
        })
        .to(
          '#code path',
          {
            opacity: 1,
            stagger: 0.001,
          },
          '<-=0.5'
        )
        .to(
          '#sdk-text',
          {
            opacity: 1,
          },
          '<'
        );
    }, containerRef);

    return () => ctx.revert();
  }, [containerRef]);

  return (
    <div className={styles.animation} ref={trigger}>
      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="-80 50 750 650">
        <g id="mobile-visual">
          <g id="phone">
            <rect
              id="Rectangle 14599"
              width="255"
              height="488.931"
              x="176.5"
              y="145.5"
              fill="#000"
              stroke="#FAFAFA"
              rx="18.5"
            ></rect>
            <rect
              id="Rectangle 14600"
              width="239.448"
              height="474.483"
              x="184.828"
              y="152.724"
              fill="#252424"
              rx="14"
            ></rect>
            <use href="#mobile-image"></use>
            <rect
              id="phone-faded"
              className={styles.phoneFaded}
              width="239.448"
              height="474.483"
              x="184.828"
              y="152.724"
              fill="#252424"
              fillOpacity="0.8"
              rx="14"
            ></rect>
          </g>
          <g id="filter" className={styles.filter} style={{ '--x': ' 100px' }}>
            <rect
              x="185.5"
              y="153.5"
              width="238.448"
              height="473.483"
              rx="13.5"
              fill="black"
              fillOpacity="0.2"
              stroke="#9E36F1"
              stroke-width="2"
            />
          </g>
          <g id="sdk-squares" className={styles.sdkSquares}>
            <path
              id="Vector 32"
              fill="url(#paint1_linear_849_9498)"
              fillOpacity="0.6"
              d="M241 128.74l68.639-34.78 69.325 34.78-69.325 34.436L241 128.74z"
              opacity="0.8"
            ></path>
            <path
              id="Vector 33"
              fill="url(#paint2_linear_849_9498)"
              d="M241 86.603l68.639-34.779 69.325 34.78-69.325 34.436L241 86.603z"
              opacity="0.9"
            ></path>
            <path
              id="Vector 27"
              fill="#07C799"
              d="M241 44.858L309.639 10l69.325 34.858-69.325 34.514L241 44.858z"
              opacity="0.9"
            ></path>
          </g>
          <g style={{ '--x': '200px' }} id="scan" className={styles.scan}>
            <rect
              width="239.448"
              height="474.483"
              x="185"
              y="153"
              fill="url(#paint0_radial_923_51700)"
              rx="14"
              fillOpacity="0.5"
            />
            <rect
              width="239.448"
              height="474.483"
              x="185"
              y="153"
              fill="url(#paint1_radial_923_51700)"
              rx="14"
              fillOpacity="0.5"
            />
            <rect
              width="239.448"
              height="474.483"
              x="185"
              y="153"
              fill="url(#paint2_radial_923_51700)"
              rx="14"
              fillOpacity="0.5"
            />
            <rect
              width="239.448"
              height="474.483"
              x="185"
              y="153"
              fill="url(#paint3_radial_923_51700)"
              rx="14"
              fillOpacity="0.5"
            />
          </g>
          <path
            id="scan-line"
            style={{ '--x': '220px' }}
            className={styles.scanLine}
            fill="#07C799"
            d="M162 135h285v2H162z"
          ></path>
          <g id="code" className={styles.code}>
            <path d="M259.678 354.437L254 357.716v1.606l5.678-3.278v-1.607z"></path>
            <path d="M267.164 350.115l-5.678 3.279V355l5.678-3.278v-1.607z"></path>
            <path d="M274.65 345.793l-5.678 3.279v1.606l5.678-3.278v-1.607z"></path>
            <path d="M282.136 341.471l-5.678 3.279v1.606l5.678-3.279v-1.606z"></path>
            <path d="M293.92 334.668l-5.679 3.279v1.606l5.679-3.279v-1.606z"></path>
            <path d="M301.405 330.346l-5.678 3.279v1.606l5.678-3.279v-1.606z"></path>
            <path d="M308.891 326.024l-5.678 3.279v1.606l5.678-3.279v-1.606z"></path>
            <path d="M320.675 319.221l-5.678 3.278v1.606l5.678-3.278v-1.606z"></path>
            <path d="M328.161 314.899l-5.678 3.278v1.606l5.678-3.278v-1.606z"></path>
            <path d="M339.944 308.096l-5.678 3.278v1.606l5.678-3.278v-1.606z"></path>
            <path d="M351.728 301.293l-5.679 3.278v1.606l5.679-3.278v-1.606z"></path>
            <path d="M359.214 296.971l-5.679 3.278v1.606l5.679-3.278v-1.606z"></path>
            <path d="M370.997 290.168l-5.678 3.278v1.606l5.678-3.278v-1.606z"></path>
            <path d="M382.78 283.364l-5.678 3.279v1.606l5.678-3.279v-1.606z"></path>
            <path d="M394.564 276.561l-5.678 3.279v1.606l5.678-3.279v-1.606z"></path>
            <path d="M259.678 360.437L254 363.715v1.606l5.678-3.278v-1.606z"></path>
            <path d="M271.462 353.634l-5.679 3.278v1.606l5.679-3.278v-1.606z"></path>
            <path d="M283.245 346.831l-5.678 3.278v1.606l5.678-3.278v-1.606z"></path>
            <path d="M290.731 342.509l-5.678 3.278v1.606l5.678-3.278v-1.606z"></path>
            <path d="M298.217 338.187l-5.678 3.278v1.606l5.678-3.278v-1.606z"></path>
            <path d="M305.703 333.865l-5.678 3.278v1.606l5.678-3.278v-1.606z"></path>
            <path d="M317.486 327.061l-5.678 3.279v1.606l5.678-3.279v-1.606z"></path>
            <path d="M329.27 320.258l-5.678 3.279v1.606l5.678-3.279v-1.606z"></path>
            <path d="M341.053 313.455l-5.678 3.278v1.606l5.678-3.278v-1.606z"></path>
            <path d="M352.837 306.652l-5.679 3.278v1.606l5.679-3.278v-1.606z"></path>
            <path d="M360.323 302.33l-5.679 3.278v1.606l5.679-3.278v-1.606z"></path>
            <path d="M367.809 298.008l-5.679 3.278v1.606l5.679-3.278v-1.606z"></path>
            <path d="M379.592 291.205l-5.678 3.278v1.606l5.678-3.278v-1.606z"></path>
            <path d="M391.375 284.402l-5.678 3.278v1.606l5.678-3.278v-1.606z"></path>
            <path d="M259.678 366.083L254 369.362v1.606l5.678-3.278v-1.607z"></path>
            <path d="M271.462 359.28l-5.679 3.279v1.606l5.679-3.279v-1.606z"></path>
            <path d="M283.245 352.477l-5.678 3.278v1.607l5.678-3.279v-1.606z"></path>
            <path d="M295.029 345.674l-5.679 3.278v1.606l5.679-3.278v-1.606z"></path>
            <path d="M306.812 338.871l-5.678 3.278v1.606l5.678-3.278v-1.606z"></path>
            <path d="M318.595 332.068l-5.678 3.278v1.606l5.678-3.278v-1.606z"></path>
            <path d="M334.676 322.783l-5.678 3.279v1.606l5.678-3.279v-1.606z"></path>
            <path d="M346.46 315.98l-5.678 3.278v1.607l5.678-3.279v-1.606z"></path>
            <path d="M358.243 309.177l-5.678 3.278v1.606l5.678-3.278v-1.606z"></path>
            <path d="M370.027 302.374l-5.679 3.278v1.606l5.679-3.278v-1.606z"></path>
            <path d="M394.703 288.127l-5.679 3.279v1.606l5.679-3.279v-1.606z"></path>
            <path d="M259.678 372.083L254 375.361v1.606l5.678-3.278v-1.606z"></path>
            <path d="M271.462 365.28l-5.679 3.278v1.606l5.679-3.278v-1.606z"></path>
            <path d="M283.245 358.477l-5.678 3.278v1.606l5.678-3.278v-1.606z"></path>
            <path d="M295.029 351.673l-5.679 3.279v1.606l5.679-3.279v-1.606z"></path>
            <path d="M306.812 344.87l-5.678 3.279v1.606l5.678-3.279v-1.606z"></path>
            <path d="M318.595 338.067l-5.678 3.278v1.606l5.678-3.278v-1.606z"></path>
            <path d="M330.379 331.264l-5.678 3.278v1.606l5.678-3.278v-1.606z"></path>
            <path d="M342.162 324.461l-5.678 3.278v1.606l5.678-3.278v-1.606z"></path>
            <path d="M353.946 317.658l-5.679 3.278v1.606l5.679-3.278v-1.606z"></path>
            <path d="M361.432 313.336l-5.679 3.278v1.606l5.679-3.278v-1.606z"></path>
            <path d="M368.918 309.014l-5.679 3.278v1.606l5.679-3.278v-1.606z"></path>
            <path d="M376.404 304.692l-5.679 3.278v1.606l5.679-3.278v-1.606z"></path>
            <path d="M259.678 377.729L254 381.008v1.606l5.678-3.278v-1.607z"></path>
            <path d="M267.164 373.407l-5.678 3.279v1.606l5.678-3.279v-1.606z"></path>
            <path d="M274.65 369.085l-5.678 3.279v1.606l5.678-3.279v-1.606z"></path>
            <path d="M286.434 362.282l-5.679 3.279v1.606l5.679-3.279v-1.606z"></path>
            <path d="M293.92 357.96l-5.679 3.279v1.606l5.679-3.279v-1.606z"></path>
            <path d="M305.703 351.157l-5.678 3.278v1.606l5.678-3.278v-1.606z"></path>
            <path d="M317.486 344.354l-5.678 3.278v1.606l5.678-3.278v-1.606z"></path>
            <path d="M324.972 340.032l-5.678 3.278v1.606l5.678-3.278v-1.606z"></path>
            <path d="M336.756 333.229l-5.678 3.278v1.606l5.678-3.278v-1.606z"></path>
            <path d="M348.539 326.426l-5.678 3.278v1.606l5.678-3.278v-1.606z"></path>
            <path d="M360.323 319.622l-5.679 3.279v1.606l5.679-3.279v-1.606z"></path>
            <path d="M372.106 312.819l-5.678 3.279v1.606l5.678-3.279v-1.606z"></path>
            <path d="M383.889 306.016l-5.678 3.278v1.606l5.678-3.278v-1.606z"></path>
            <path d="M259.678 383.729L254 387.007v1.606l5.678-3.278v-1.606z"></path>
            <path d="M267.164 379.407l-5.678 3.278v1.606l5.678-3.278v-1.606z"></path>
            <path d="M274.65 375.085l-5.678 3.278v1.606l5.678-3.278v-1.606z"></path>
            <path d="M282.136 370.763l-5.678 3.278v1.606l5.678-3.278v-1.606z"></path>
            <path d="M293.92 363.96l-5.679 3.278v1.606l5.679-3.278v-1.606z"></path>
            <path d="M305.703 357.157l-5.678 3.278v1.606l5.678-3.278v-1.606z"></path>
            <path d="M317.486 350.353l-5.678 3.279v1.606l5.678-3.279v-1.606z"></path>
            <path d="M329.27 343.55l-5.678 3.279v1.606l5.678-3.279v-1.606z"></path>
            <path d="M336.756 339.228l-5.678 3.279v1.606l5.678-3.279v-1.606z"></path>
            <path d="M344.242 334.906l-5.679 3.278v1.607l5.679-3.279v-1.606z"></path>
            <path d="M356.025 328.103l-5.678 3.278v1.606l5.678-3.278v-1.606z"></path>
            <path d="M367.809 321.3l-5.679 3.278v1.606l5.679-3.278V321.3z"></path>
            <path d="M379.592 314.497l-5.678 3.278v1.606l5.678-3.278v-1.606z"></path>
            <path d="M391.375 307.693l-5.678 3.279v1.606l5.678-3.278v-1.607z"></path>
            <path d="M259.678 389.375L254 392.654v1.606l5.678-3.279v-1.606z"></path>
            <path d="M271.462 382.572l-5.679 3.279v1.606l5.679-3.279v-1.606z"></path>
            <path d="M283.245 375.769l-5.678 3.278v1.606l5.678-3.278v-1.606z"></path>
            <path d="M295.029 368.966l-5.679 3.278v1.606l5.679-3.278v-1.606z"></path>
            <path d="M311.109 359.682l-5.678 3.278v1.606l5.678-3.278v-1.606z"></path>
            <path d="M322.893 352.878l-5.678 3.279v1.606l5.678-3.279v-1.606z"></path>
            <path d="M334.676 346.075l-5.678 3.279v1.606l5.678-3.279v-1.606z"></path>
            <path d="M346.46 339.272l-5.678 3.278v1.606l5.678-3.278v-1.606z"></path>
            <path d="M371.136 325.025l-5.679 3.279v1.606l5.679-3.278v-1.607z"></path>
            <path d="M391.514 313.26l-5.678 3.278v1.606l5.678-3.278v-1.606z"></path>
            <path d="M259.678 395.022L254 398.3v1.606l5.678-3.278v-1.606z"></path>
            <path d="M271.462 388.219l-5.679 3.278v1.606l5.679-3.278v-1.606z"></path>
            <path d="M283.245 381.416l-5.678 3.278v1.606l5.678-3.278v-1.606z"></path>
            <path d="M295.029 374.612l-5.679 3.279v1.606l5.679-3.278v-1.607z"></path>
            <path d="M306.812 367.809l-5.678 3.279v1.606l5.678-3.279v-1.606z"></path>
            <path d="M318.595 361.006l-5.678 3.278v1.607l5.678-3.279v-1.606z"></path>
            <path d="M330.379 354.203l-5.678 3.278v1.606l5.678-3.278v-1.606z"></path>
            <path d="M342.162 347.4l-5.678 3.278v1.606l5.678-3.278V347.4z"></path>
            <path d="M349.648 343.078l-5.678 3.278v1.606l5.678-3.278v-1.606z"></path>
            <path d="M357.134 338.756l-5.678 3.278v1.606l5.678-3.278v-1.606z"></path>
            <path d="M364.62 334.434l-5.678 3.278v1.606l5.678-3.278v-1.606z"></path>
            <path d="M376.404 327.631l-5.679 3.278v1.606l5.679-3.278v-1.606z"></path>
            <path d="M383.889 323.309l-5.678 3.278v1.606l5.678-3.278v-1.606z"></path>
            <path d="M391.375 318.987l-5.678 3.278v1.606l5.678-3.278v-1.606z"></path>
            <path d="M259.678 401.021L254 404.3v1.606l5.678-3.279v-1.606z"></path>
            <path d="M267.164 396.699l-5.678 3.279v1.606l5.678-3.279v-1.606z"></path>
            <path d="M278.948 389.896l-5.679 3.279v1.606l5.679-3.279v-1.606z"></path>
            <path d="M290.731 383.093l-5.678 3.278v1.606l5.678-3.278v-1.606z"></path>
            <path d="M298.217 378.771l-5.678 3.278v1.606l5.678-3.278v-1.606z"></path>
            <path d="M310 371.968l-5.678 3.278v1.606l5.678-3.278v-1.606z"></path>
            <path d="M321.784 365.165l-5.678 3.278v1.606l5.678-3.278v-1.606z"></path>
            <path d="M333.567 358.362l-5.678 3.278v1.606l5.678-3.278v-1.606z"></path>
            <path d="M345.351 351.558l-5.678 3.279v1.606l5.678-3.279v-1.606z"></path>
            <path d="M357.134 344.755l-5.678 3.279v1.606l5.678-3.279v-1.606z"></path>
            <path d="M368.918 337.952l-5.679 3.278v1.606l5.679-3.278v-1.606z"></path>
            <path d="M376.404 333.63l-5.679 3.278v1.606l5.679-3.278v-1.606z"></path>
            <path d="M383.889 329.308l-5.678 3.278v1.606l5.678-3.278v-1.606z"></path>
            <path d="M391.375 324.986l-5.678 3.278v1.606l5.678-3.278v-1.606z"></path>
            <path d="M259.678 406.668L254 409.946v1.606l5.678-3.278v-1.606z"></path>
            <path d="M271.462 399.865l-5.679 3.278v1.606l5.679-3.278v-1.606z"></path>
            <path d="M283.245 393.062l-5.678 3.278v1.606l5.678-3.278v-1.606z"></path>
            <path d="M295.029 386.258l-5.679 3.279v1.606l5.679-3.278v-1.607z"></path>
            <path d="M302.514 381.936l-5.678 3.279v1.606l5.678-3.279v-1.606z"></path>
            <path d="M310 377.614l-5.678 3.279v1.606L310 379.22v-1.606z"></path>
            <path d="M321.784 370.811l-5.678 3.279v1.606l5.678-3.279v-1.606z"></path>
            <path d="M333.567 364.008l-5.678 3.278v1.606l5.678-3.278v-1.606z"></path>
            <path d="M345.351 357.205l-5.678 3.278v1.606l5.678-3.278v-1.606z"></path>
            <path d="M357.134 350.402l-5.678 3.278v1.606l5.678-3.278v-1.606z"></path>
            <path d="M368.918 343.599l-5.679 3.278v1.606l5.679-3.278v-1.606z"></path>
            <path d="M380.701 336.795l-5.678 3.279v1.606l5.678-3.279v-1.606z"></path>
            <path d="M392.484 329.992l-5.678 3.279v1.606l5.678-3.279v-1.606z"></path>
            <path d="M259.678 412.667L254 415.946v1.606l5.678-3.279v-1.606z"></path>
            <path d="M275.759 403.383l-5.678 3.278v1.606l5.678-3.278v-1.606z"></path>
            <path d="M287.543 396.58l-5.679 3.278v1.606l5.679-3.278v-1.606z"></path>
            <path d="M299.326 389.777l-5.678 3.278v1.606l5.678-3.278v-1.606z"></path>
            <path d="M311.109 382.974l-5.678 3.278v1.606l5.678-3.278v-1.606z"></path>
            <path d="M335.785 368.727l-5.678 3.278v1.606l5.678-3.278v-1.606z"></path>
            <path d="M356.164 356.961l-5.678 3.279v1.606l5.678-3.278v-1.607z"></path>
            <path d="M367.947 350.158l-5.678 3.279v1.606l5.678-3.279v-1.606z"></path>
            <path d="M379.731 343.355l-5.679 3.278v1.607l5.679-3.279v-1.606z"></path>
            <path d="M391.514 336.552l-5.678 3.278v1.606l5.678-3.278v-1.606z"></path>
            <path d="M259.678 418.314L254 421.592v1.606l5.678-3.278v-1.606z"></path>
            <path d="M271.462 411.511l-5.679 3.278v1.606l5.679-3.278v-1.606z"></path>
            <path d="M283.245 404.708l-5.678 3.278v1.606l5.678-3.278v-1.606z"></path>
            <path d="M295.029 397.904l-5.679 3.279v1.606l5.679-3.279v-1.606z"></path>
            <path d="M306.812 391.101l-5.678 3.279v1.606l5.678-3.279v-1.606z"></path>
            <path d="M314.298 386.779l-5.678 3.279v1.606l5.678-3.279v-1.606z"></path>
            <path d="M321.784 382.457l-5.678 3.279v1.606l5.678-3.279v-1.606z"></path>
            <path d="M329.27 378.135l-5.678 3.279v1.606l5.678-3.279v-1.606z"></path>
            <path d="M341.053 371.332l-5.678 3.278v1.606l5.678-3.278v-1.606z"></path>
            <path d="M348.539 367.01l-5.678 3.278v1.606l5.678-3.278v-1.606z"></path>
            <path d="M356.025 362.688l-5.678 3.278v1.606l5.678-3.278v-1.606z"></path>
            <path d="M367.809 355.885l-5.679 3.278v1.606l5.679-3.278v-1.606z"></path>
            <path d="M375.295 351.563l-5.679 3.278v1.606l5.679-3.278v-1.606z"></path>
            <path d="M387.078 344.76l-5.678 3.278v1.606l5.678-3.278v-1.606z"></path>
            <path d="M259.678 424.313L254 427.592v1.606l5.678-3.279v-1.606z"></path>
            <path d="M267.164 419.991l-5.678 3.279v1.606l5.678-3.279v-1.606z"></path>
            <path d="M278.948 413.188l-5.679 3.279v1.606l5.679-3.279v-1.606z"></path>
            <path d="M290.731 406.385l-5.678 3.278v1.606l5.678-3.278v-1.606z"></path>
            <path d="M302.514 399.582l-5.678 3.278v1.606l5.678-3.278v-1.606z"></path>
            <path d="M314.298 392.779l-5.678 3.278v1.606l5.678-3.278v-1.606z"></path>
            <path d="M326.081 385.976l-5.678 3.278v1.606l5.678-3.278v-1.606z"></path>
            <path d="M337.865 379.172l-5.678 3.279v1.606l5.678-3.279v-1.606z"></path>
            <path d="M345.351 374.85l-5.678 3.279v1.606l5.678-3.279v-1.606z"></path>
            <path d="M352.837 370.528l-5.679 3.279v1.606l5.679-3.279v-1.606z"></path>
            <path d="M360.323 366.206l-5.679 3.279v1.606l5.679-3.279v-1.606z"></path>
            <path d="M372.106 359.403l-5.678 3.278v1.607l5.678-3.279v-1.606z"></path>
            <path d="M383.889 352.6l-5.678 3.278v1.606l5.678-3.278V352.6z"></path>
            <path d="M395.673 345.797l-5.678 3.278v1.606l5.678-3.278v-1.606z"></path>
            <path d="M259.678 429.96L254 433.238v1.606l5.678-3.278v-1.606z"></path>
            <path d="M267.164 425.638l-5.678 3.278v1.606l5.678-3.278v-1.606z"></path>
            <path d="M274.65 421.316l-5.678 3.278v1.606l5.678-3.278v-1.606z"></path>
            <path d="M286.434 414.513l-5.679 3.278v1.606l5.679-3.278v-1.606z"></path>
            <path d="M298.217 407.71l-5.678 3.278v1.606l5.678-3.278v-1.606z"></path>
            <path d="M310 400.906l-5.678 3.279v1.606l5.678-3.279v-1.606z"></path>
            <path d="M321.784 394.103l-5.678 3.279v1.606l5.678-3.279v-1.606z"></path>
            <path d="M333.567 387.3l-5.678 3.278v1.606l5.678-3.278V387.3z"></path>
            <path d="M345.351 380.497l-5.678 3.278v1.606l5.678-3.278v-1.606z"></path>
            <path d="M357.134 373.694l-5.678 3.278v1.606l5.678-3.278v-1.606z"></path>
            <path d="M368.918 366.891l-5.679 3.278v1.606l5.679-3.278v-1.606z"></path>
            <path d="M384.999 357.606l-5.679 3.279v1.606l5.679-3.279v-1.606z"></path>
            <path d="M396.782 350.803l-5.678 3.278v1.606l5.678-3.278v-1.606z"></path>
            <path d="M259.678 435.606L254 438.885v1.606l5.678-3.278v-1.607z"></path>
            <path d="M271.462 428.803l-5.679 3.279v1.606l5.679-3.279v-1.606z"></path>
            <path d="M296.138 414.557l-5.679 3.278v1.606l5.679-3.278v-1.606z"></path>
            <path d="M316.516 402.791l-5.678 3.279v1.606l5.678-3.279v-1.606z"></path>
            <path d="M328.299 395.988l-5.678 3.278v1.606l5.678-3.278v-1.606z"></path>
            <path d="M340.083 389.185l-5.678 3.278v1.606l5.678-3.278v-1.606z"></path>
            <path d="M351.866 382.382l-5.678 3.278v1.606l5.678-3.278v-1.606z"></path>
            <path d="M363.65 375.579l-5.679 3.278v1.606l5.679-3.278v-1.606z"></path>
            <path d="M375.433 368.775l-5.678 3.279v1.606l5.678-3.279v-1.606z"></path>
            <path d="M387.217 361.972l-5.679 3.279v1.606l5.679-3.279v-1.606z"></path>
            <path d="M399 355.169l-5.678 3.278v1.606l5.678-3.278v-1.606z"></path>
            <path d="M259.678 441.606L254 444.884v1.606l5.678-3.278v-1.606z"></path>
            <path d="M267.164 437.284l-5.678 3.278v1.606l5.678-3.278v-1.606z"></path>
            <path d="M274.65 432.962l-5.678 3.278v1.606l5.678-3.278v-1.606z"></path>
            <path d="M282.136 428.64l-5.678 3.278v1.606l5.678-3.278v-1.606z"></path>
            <path d="M293.92 421.837l-5.679 3.278v1.606l5.679-3.278v-1.606z"></path>
            <path d="M301.405 417.515l-5.678 3.278v1.606l5.678-3.278v-1.606z"></path>
            <path d="M308.891 413.193l-5.678 3.278v1.606l5.678-3.278v-1.606z"></path>
            <path d="M320.675 406.39l-5.678 3.278v1.606l5.678-3.278v-1.606z"></path>
            <path d="M328.161 402.068l-5.678 3.278v1.606l5.678-3.278v-1.606z"></path>
            <path d="M339.944 395.264l-5.678 3.279v1.606l5.678-3.279v-1.606z"></path>
            <path d="M351.728 388.461l-5.679 3.279v1.606l5.679-3.279v-1.606z"></path>
            <path d="M359.214 384.139l-5.679 3.278v1.607l5.679-3.279v-1.606z"></path>
            <path d="M370.997 377.336l-5.678 3.278v1.606l5.678-3.278v-1.606z"></path>
            <path d="M382.78 370.533l-5.678 3.278v1.606l5.678-3.278v-1.606z"></path>
            <path d="M394.564 363.73l-5.678 3.278v1.606l5.678-3.278v-1.606z"></path>
            <path d="M259.678 447.252L254 450.531v1.606l5.678-3.278v-1.607z"></path>
            <path d="M271.462 440.449l-5.679 3.279v1.606l5.679-3.279v-1.606z"></path>
            <path d="M283.245 433.646l-5.678 3.278v1.607l5.678-3.279v-1.606z"></path>
            <path d="M290.731 429.324l-5.678 3.278v1.606l5.678-3.278v-1.606z"></path>
            <path d="M298.217 425.002l-5.678 3.278v1.606l5.678-3.278v-1.606z"></path>
            <path d="M305.703 420.68l-5.678 3.278v1.606l5.678-3.278v-1.606z"></path>
            <path d="M317.486 413.877l-5.678 3.278v1.606l5.678-3.278v-1.606z"></path>
            <path d="M329.27 407.074l-5.678 3.278v1.606l5.678-3.278v-1.606z"></path>
            <path d="M341.053 400.271l-5.678 3.278v1.606l5.678-3.278v-1.606z"></path>
            <path d="M352.837 393.467l-5.679 3.279v1.606l5.679-3.279v-1.606z"></path>
            <path d="M360.323 389.145l-5.679 3.279v1.606l5.679-3.279v-1.606z"></path>
            <path d="M367.809 384.823l-5.679 3.279v1.606l5.679-3.279v-1.606z"></path>
            <path d="M379.592 378.02l-5.678 3.279v1.606l5.678-3.279v-1.606z"></path>
            <path d="M391.375 371.217l-5.678 3.278v1.606l5.678-3.278v-1.606z"></path>
            <path d="M259.678 453.252L254 456.53v1.606l5.678-3.278v-1.606z"></path>
            <path d="M271.462 446.449l-5.679 3.278v1.606l5.679-3.278v-1.606z"></path>
            <path d="M283.245 439.646l-5.678 3.278v1.606l5.678-3.278v-1.606z"></path>
            <path d="M295.029 432.842l-5.679 3.279v1.606l5.679-3.279v-1.606z"></path>
            <path d="M306.812 426.039l-5.678 3.279v1.606l5.678-3.279v-1.606z"></path>
            <path d="M318.595 419.236l-5.678 3.278v1.606l5.678-3.278v-1.606z"></path>
            <path d="M334.676 409.952l-5.678 3.278v1.606l5.678-3.278v-1.606z"></path>
            <path d="M346.46 403.149l-5.678 3.278v1.606l5.678-3.278v-1.606z"></path>
            <path d="M358.243 396.345l-5.678 3.279v1.606l5.678-3.279v-1.606z"></path>
            <path d="M370.027 389.542l-5.679 3.279v1.606l5.679-3.279v-1.606z"></path>
            <path d="M394.703 375.296l-5.679 3.278v1.606l5.679-3.278v-1.606z"></path>
            <path d="M259.678 458.898L254 462.177v1.606l5.678-3.278v-1.607z"></path>
            <path d="M271.462 452.095l-5.679 3.279v1.606l5.679-3.279v-1.606z"></path>
            <path d="M283.245 445.292l-5.678 3.278v1.606l5.678-3.278v-1.606z"></path>
            <path d="M295.029 438.489l-5.679 3.278v1.606l5.679-3.278v-1.606z"></path>
            <path d="M306.812 431.686l-5.678 3.278v1.606l5.678-3.278v-1.606z"></path>
            <path d="M318.595 424.883l-5.678 3.278v1.606l5.678-3.278v-1.606z"></path>
            <path d="M330.379 418.079l-5.678 3.279v1.606l5.678-3.279v-1.606z"></path>
            <path d="M342.162 411.276l-5.678 3.279v1.606l5.678-3.279v-1.606z"></path>
            <path d="M353.946 404.473l-5.679 3.278v1.606l5.679-3.278v-1.606z"></path>
            <path d="M361.432 400.151l-5.679 3.278v1.606l5.679-3.278v-1.606z"></path>
            <path d="M368.918 395.829l-5.679 3.278v1.606l5.679-3.278v-1.606z"></path>
            <path d="M376.404 391.507l-5.679 3.278v1.606l5.679-3.278v-1.606z"></path>
            <path d="M259.678 464.898L254 468.176v1.606l5.678-3.278v-1.606z"></path>
            <path d="M267.164 460.576l-5.678 3.278v1.606l5.678-3.278v-1.606z"></path>
            <path d="M274.65 456.254l-5.678 3.278v1.606l5.678-3.278v-1.606z"></path>
            <path d="M286.434 449.451l-5.679 3.278v1.606l5.679-3.278v-1.606z"></path>
            <path d="M293.92 445.129l-5.679 3.278v1.606l5.679-3.278v-1.606z"></path>
            <path d="M305.703 438.326l-5.678 3.278v1.606l5.678-3.278v-1.606z"></path>
            <path d="M317.486 431.522l-5.678 3.279v1.606l5.678-3.279v-1.606z"></path>
            <path d="M324.972 427.2l-5.678 3.279v1.606l5.678-3.279V427.2z"></path>
            <path d="M336.756 420.397l-5.678 3.279v1.606l5.678-3.279v-1.606z"></path>
            <path d="M348.539 413.594l-5.678 3.278v1.606l5.678-3.278v-1.606z"></path>
            <path d="M360.323 406.791l-5.679 3.278v1.606l5.679-3.278v-1.606z"></path>
            <path d="M372.106 399.988l-5.678 3.278v1.606l5.678-3.278v-1.606z"></path>
            <path d="M383.889 393.185l-5.678 3.278v1.606l5.678-3.278v-1.606z"></path>
            <path d="M259.678 470.544L254 473.823v1.606l5.678-3.279v-1.606z"></path>
            <path d="M267.164 466.222l-5.678 3.279v1.606l5.678-3.279v-1.606z"></path>
            <path d="M274.65 461.9l-5.678 3.279v1.606l5.678-3.279V461.9z"></path>
            <path d="M282.136 457.578l-5.678 3.279v1.606l5.678-3.279v-1.606z"></path>
            <path d="M293.92 450.775l-5.679 3.279v1.606l5.679-3.279v-1.606z"></path>
            <path d="M305.703 443.972l-5.678 3.278v1.606l5.678-3.278v-1.606z"></path>
            <path d="M317.486 437.169l-5.678 3.278v1.606l5.678-3.278v-1.606z"></path>
            <path d="M329.27 430.366l-5.678 3.278v1.606l5.678-3.278v-1.606z"></path>
            <path d="M336.756 426.044l-5.678 3.278v1.606l5.678-3.278v-1.606z"></path>
            <path d="M344.242 421.722L338.563 425v1.606l5.679-3.278v-1.606z"></path>
            <path d="M356.025 414.919l-5.678 3.278v1.606l5.678-3.278v-1.606z"></path>
            <path d="M367.809 408.115l-5.679 3.279V413l5.679-3.279v-1.606z"></path>
            <path d="M379.592 401.312l-5.678 3.279v1.606l5.678-3.279v-1.606z"></path>
            <path d="M391.375 394.509l-5.678 3.278v1.606l5.678-3.278v-1.606z"></path>
            <path d="M259.678 476.191L254 479.469v1.606l5.678-3.278v-1.606z"></path>
            <path d="M271.462 469.388l-5.679 3.278v1.606l5.679-3.278v-1.606z"></path>
            <path d="M283.245 462.585l-5.678 3.278v1.606l5.678-3.278v-1.606z"></path>
            <path d="M295.029 455.781l-5.679 3.279v1.606l5.679-3.278v-1.607z"></path>
            <path d="M311.109 446.497l-5.678 3.278v1.607l5.678-3.279v-1.606z"></path>
            <path d="M322.893 439.694l-5.678 3.278v1.606l5.678-3.278v-1.606z"></path>
            <path d="M334.676 432.891l-5.678 3.278v1.606l5.678-3.278v-1.606z"></path>
            <path d="M346.46 426.088l-5.678 3.278v1.606l5.678-3.278v-1.606z"></path>
            <path d="M371.136 411.841l-5.679 3.278v1.606l5.679-3.278v-1.606z"></path>
            <path d="M391.514 400.076l-5.678 3.278v1.606l5.678-3.278v-1.606z"></path>
            <path d="M259.678 482.19L254 485.469v1.606l5.678-3.279v-1.606z"></path>
            <path d="M271.462 475.387l-5.679 3.279v1.606l5.679-3.279v-1.606z"></path>
            <path d="M283.245 468.584l-5.678 3.278v1.606l5.678-3.278v-1.606z"></path>
            <path d="M295.029 461.781l-5.679 3.278v1.606l5.679-3.278v-1.606z"></path>
            <path d="M306.812 454.978l-5.678 3.278v1.606l5.678-3.278v-1.606z"></path>
            <path d="M318.595 448.175l-5.678 3.278v1.606l5.678-3.278v-1.606z"></path>
            <path d="M330.379 441.371l-5.678 3.279v1.606l5.678-3.279v-1.606z"></path>
          </g>
          <rect
            id="dom"
            style={{ '--x': '300' }}
            className={styles.dom}
            width="239.448"
            height="474.483"
            x="186"
            y="153"
            fill="#fff"
            fillOpacity="0.3"
            rx="14"
          ></rect>
          <g id="crash-alert" style={{ '--x': '100px' }} className={styles.crashAlert}>
            <rect id="Rectangle 14609" width="92" height="81" x="397" y="318" fill="#fff" rx="8"></rect>
            <g id="Icon">
              <path
                id="Vector"
                fill="url(#paint293_linear_849_9498)"
                d="M441.835 335.175a1.482 1.482 0 012.582 0l14.308 25.064 14.307 25.056c.574 1.005-.143 2.261-1.29 2.261H414.51c-1.147 0-1.864-1.256-1.29-2.261l14.307-25.056 14.308-25.064z"
              ></path>
              <path
                id="Vector (Stroke)"
                fill="#fff"
                fillRule="evenodd"
                d="M443.004 348.433c.823 0 1.49.675 1.49 1.507v18.055c0 .833-.667 1.507-1.49 1.507s-1.491-.674-1.491-1.507V349.94a1.5 1.5 0 011.491-1.507z"
                clipRule="evenodd"
              ></path>
              <path
                id="Vector (Stroke)_2"
                fill="#fff"
                fillRule="evenodd"
                d="M443.004 378.581a1.5 1.5 0 01-1.491-1.508v-1.507a1.5 1.5 0 011.491-1.508 1.5 1.5 0 011.491 1.508v1.507a1.5 1.5 0 01-1.491 1.508z"
                clipRule="evenodd"
              ></path>
              <path
                id="Vector (Stroke)_3"
                fill="#F5AE3D"
                fillRule="evenodd"
                d="M467.912 334.308a1.52 1.52 0 01-.181 2.124l-4.229 3.598.823 1.424a1.52 1.52 0 01-.367 1.946l-5.814 4.616a1.48 1.48 0 01-2.093-.255 1.519 1.519 0 01.252-2.117l4.792-3.804-.8-1.385a1.52 1.52 0 01.327-1.914l5.19-4.416a1.48 1.48 0 012.1.183z"
                clipRule="evenodd"
              ></path>
              <path
                id="Vector (Stroke)_4"
                fill="#F5AE3D"
                fillRule="evenodd"
                d="M482.651 328.538a1.52 1.52 0 01-.183 2.124l-4.227 3.59.821 1.423c.376.65.22 1.48-.365 1.945l-5.815 4.624a1.48 1.48 0 01-2.093-.254 1.518 1.518 0 01.25-2.116l4.794-3.812-.8-1.386a1.518 1.518 0 01.328-1.915l5.189-4.408a1.48 1.48 0 012.101.185z"
                clipRule="evenodd"
              ></path>
              <path
                id="Vector (Stroke)_5"
                fill="#F5AE3D"
                fillRule="evenodd"
                d="M418.087 334.309c.53-.637 1.47-.72 2.1-.184l5.197 4.415a1.52 1.52 0 01.329 1.915l-.8 1.385 4.792 3.804c.647.515.76 1.462.252 2.117s-1.446.769-2.093.255l-5.814-4.616a1.518 1.518 0 01-.367-1.946l.822-1.423-4.235-3.598a1.52 1.52 0 01-.183-2.124z"
                clipRule="evenodd"
              ></path>
              <path
                id="Vector (Stroke)_6"
                fill="#F5AE3D"
                fillRule="evenodd"
                d="M403.349 328.538a1.48 1.48 0 012.1-.185l5.19 4.408a1.52 1.52 0 01.33 1.912l-.798 1.388 4.79 3.813a1.52 1.52 0 01.249 2.117 1.48 1.48 0 01-2.093.252l-5.807-4.624a1.518 1.518 0 01-.367-1.941l.818-1.424-4.229-3.592a1.52 1.52 0 01-.183-2.124z"
                clipRule="evenodd"
              ></path>
            </g>
          </g>
          <g id="scan-text" className={styles.scanText}>
            <path id="Vector 432" stroke="#07C799" strokeWidth="2" d="M401 23h-91v102"></path>
            <text fill="#fff" fontSize="14" x="416" y="9.7565">
              Fullstory scans app{' '}
            </text>
            <text fill="#fff" fontSize="24">
              <tspan x="416" y="35.1605">
                300x per{' '}
              </tspan>
              <tspan x="416" y="57.1605">
                minute{' '}
              </tspan>
            </text>
          </g>
          <g id="crash-text" className={styles.crashText}>
            <text fill="#fff" fontSize="14" x="565.605" y="337.293">
              Crash Alert
            </text>
            <path id="Vector 7" stroke="#FFA12F" strokeWidth="2" d="M489 358l46.444-24.619h25.135"></path>
          </g>
          <g id="privacy-text" className={styles.privacyText}>
            <text fill="#fff" fontSize="14">
              <tspan x="202" y="106.757">
                Sensitive data never{' '}
              </tspan>
              <tspan x="202" y="125">
                leaves the app
              </tspan>
            </text>
            <g style={{ transform: 'translate(15%, 17%)' }}>
              <path fill="#FF2059" d="M9.176 247.526a4.212 4.212 0 11-7.843-3.071 4.212 4.212 0 017.843 3.071z"></path>
              <path stroke="#FF2059" strokeWidth="2" d="M73.046 1h-63a5 5 0 00-5 5v237"></path>
            </g>
          </g>
          <g id="rules" className={styles.rules}>
            <g filter="url(#filter0_d_867_36444)">
              <rect width="261.495" height="125.207" x="26" y="17.111" fill="#fff" fillOpacity="0.98" rx="4.432"></rect>
              <rect
                width="264.495"
                height="128.207"
                x="24.5"
                y="15.611"
                stroke="url(#paint0_radial_867_36444)"
                strokeWidth="3"
                rx="5.932"
              ></rect>
              <rect
                width="264.495"
                height="128.207"
                x="24.5"
                y="15.611"
                stroke="url(#paint1_radial_867_36444)"
                strokeWidth="3"
                rx="5.932"
              ></rect>
              <rect
                width="264.495"
                height="128.207"
                x="24.5"
                y="15.611"
                stroke="url(#paint2_radial_867_36444)"
                strokeWidth="3"
                rx="5.932"
              ></rect>
              <rect
                width="264.495"
                height="128.207"
                x="24.5"
                y="15.611"
                stroke="url(#paint3_radial_867_36444)"
                strokeWidth="3"
                rx="5.932"
              ></rect>
            </g>
            <text fill="#000" style={{ whiteSpace: 'pre' }} fontSize="15.512" fontWeight="500" letterSpacing="0">
              <tspan x="43.728" y="45.947">
                Element data capture rules
              </tspan>
            </text>
            <rect width="85.318" height="26.043" x="182.222" y="105.743" fill="#F0F0F0" rx="1.617"></rect>
            <text fill="#000" style={{ whiteSpace: 'pre' }} fontSize="11.119" fontWeight="500" letterSpacing="0">
              <tspan x="45.944" y="85.515">
                input[type=password]
              </tspan>
            </text>
            <text fill="#000" style={{ whiteSpace: 'pre' }} fontSize="11.119" fontWeight="500" letterSpacing="0">
              <tspan x="45.944" y="122.399">
                #image_product
              </tspan>
            </text>
            <rect width="85.318" height="26.043" x="182.222" y="70" fill="#FF2059" fillOpacity="0.14" rx="1.617"></rect>
            <text fill="#FF2059" style={{ whiteSpace: 'pre' }} fontSize="11.119" fontWeight="600" letterSpacing="0">
              <tspan x="200.35" y="87.311">
                Excluded
              </tspan>
            </text>
            <text fill="#888" style={{ whiteSpace: 'pre' }} fontSize="11.119" fontWeight="600" letterSpacing="0">
              <tspan x="195.992" y="122.208">
                Unmasked
              </tspan>
            </text>
            <path stroke="#DEDEDE" strokeWidth="0.808" d="M287.495 59.756L26 59.756"></path>
          </g>
          <g id="sdk-text" className={styles.sdkText}>
            <text fill="#fff" fontSize="24">
              <tspan x="458" y="482">
                ultralight{' '}
              </tspan>
              <tspan x="458" y="510">
                SDK
              </tspan>
            </text>
            <path id="Vector 432_2" stroke="#009CE2" d="M307.5 469.5V493h138"></path>
            <text fill="#fff" fontSize="14" x="458" y="532">
              20kb per minute
            </text>
          </g>
        </g>
        <defs>
          <linearGradient
            id="paint0_linear_849_9498"
            x1="304.552"
            x2="304.552"
            y1="152.724"
            y2="627.207"
            gradientUnits="userSpaceOnUse"
          >
            <stop stopColor="#8053FF"></stop>
            <stop offset="1" stopColor="#448EE1"></stop>
          </linearGradient>
          <linearGradient
            id="paint1_linear_849_9498"
            x1="309.982"
            x2="309.982"
            y1="93.96"
            y2="163.176"
            gradientUnits="userSpaceOnUse"
          >
            <stop stop-color="#07C799" />
            <stop offset="1" stop-color="#07C799" stop-opacity=".19" />
          </linearGradient>
          <linearGradient
            id="paint2_linear_849_9498"
            x1="309.982"
            x2="309.982"
            y1="51.824"
            y2="121.04"
            gradientUnits="userSpaceOnUse"
          >
            <stop stop-color="#07C799" />
            <stop offset="1" stop-color="#07C799" stop-opacity=".19" />
          </linearGradient>
          <linearGradient
            id="paint3_linear_849_9498"
            x1="309.982"
            x2="309.982"
            y1="10"
            y2="79.372"
            gradientUnits="userSpaceOnUse"
          >
            <stop stopColor="#009CE2"></stop>
            <stop offset="1" stopColor="#009CE2"></stop>
          </linearGradient>
          <linearGradient
            id="paint293_linear_849_9498"
            x1="443.126"
            x2="443.126"
            y1="332.913"
            y2="387.556"
            gradientUnits="userSpaceOnUse"
          >
            <stop stopColor="#F5CD3D"></stop>
            <stop offset="1" stopColor="#F5AE3D"></stop>
          </linearGradient>
          <filter
            id="filter0_d_849_9498"
            width="268.186"
            height="149.54"
            x="0.73"
            y="177.447"
            colorInterpolationFilters="sRGB"
            filterUnits="userSpaceOnUse"
          >
            <feFlood floodOpacity="0" result="BackgroundImageFix"></feFlood>
            <feColorMatrix
              in="SourceAlpha"
              result="hardAlpha"
              values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
            ></feColorMatrix>
            <feOffset dy="7.717"></feOffset>
            <feGaussianBlur stdDeviation="9.646"></feGaussianBlur>
            <feColorMatrix values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.2 0"></feColorMatrix>
            <feBlend in2="BackgroundImageFix" mode="multiply" result="effect1_dropShadow_849_9498"></feBlend>
            <feBlend in="SourceGraphic" in2="effect1_dropShadow_849_9498" result="shape"></feBlend>
          </filter>
          <image
            href={MobileImage}
            id="mobile-image"
            width="289.25"
            height="573.17"
            x="201"
            y="155"
            transform="scale(0.88)"
          ></image>

          <circle id="small" cx="0" cy="0" r="2.5" />
          <circle id="medium" cx="0" cy="0" r="3" />
          <circle id="large" cx="0" cy="0" r="3.5" />

          <linearGradient id="mixed" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor="#8053FF" />
            <stop offset="100%" stopColor="#00479450" />
          </linearGradient>

          <linearGradient id="purple" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor="#422986" />
            <stop offset="100%" stopColor="rgba(66, 41, 134, 0.5)" />
          </linearGradient>

          <linearGradient id="blue" x1="0" x2="0" y1="1" y2="0">
            <stop offset="0%" stopColor="#0071EB" />
            <stop offset="100%" stopColor="rgba(0, 113, 235, 0.5) " />
          </linearGradient>

          <linearGradient id="pink" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor="#8053FF" />
            <stop offset="100%" stopColor="#F8598B" />
          </linearGradient>
          <radialGradient
            id="paint0_radial_923_51700"
            cx="0"
            cy="0"
            r="1"
            gradientTransform="rotate(-200 180 350) scale(150 600)"
            gradientUnits="userSpaceOnUse"
          >
            <stop stop-color="#FFA12F" />
            <stop offset="1" stop-color="#FFA12F" stop-opacity="0" />
          </radialGradient>
          <radialGradient
            id="paint1_radial_923_51700"
            cx="1"
            cy="0"
            r="1"
            gradientTransform="rotate(90 120 190) scale(800 100)"
            gradientUnits="userSpaceOnUse"
          >
            <stop stop-color="#FF2059" stop-opacity=".7" />
            <stop offset="1" stop-color="#FF2059" stop-opacity=".01" />
          </radialGradient>
          <radialGradient
            id="paint2_radial_923_51700"
            cx="0"
            cy="0"
            r="1"
            gradientTransform="matrix(500 400 -600 200 3 400)"
            gradientUnits="userSpaceOnUse"
          >
            <stop stop-color="#9E36F1" />
            <stop offset=".771535" stop-color="#9E36F1" stop-opacity=".05" />
          </radialGradient>
          <radialGradient
            id="paint3_radial_923_51700"
            cx="0"
            cy="0"
            r="1"
            gradientTransform="rotate(90 220 100) scale(800 100)"
            gradientUnits="userSpaceOnUse"
          >
            <stop stop-color="#07C799" />
            <stop offset="1" stop-color="#07C799" stop-opacity="0" />
          </radialGradient>

          <filter
            id="filter0_d_867_36444"
            width="311.816"
            height="175.529"
            x="0.839"
            y="0.814"
            colorInterpolationFilters="sRGB"
            filterUnits="userSpaceOnUse"
          >
            <feFlood floodOpacity="0" result="BackgroundImageFix"></feFlood>
            <feColorMatrix
              in="SourceAlpha"
              result="hardAlpha"
              values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
            ></feColorMatrix>
            <feOffset dy="8.864"></feOffset>
            <feGaussianBlur stdDeviation="11.08"></feGaussianBlur>
            <feColorMatrix values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.2 0"></feColorMatrix>
            <feBlend in2="BackgroundImageFix" mode="multiply" result="effect1_dropShadow_867_36444"></feBlend>
            <feBlend in="SourceGraphic" in2="effect1_dropShadow_867_36444" result="shape"></feBlend>
          </filter>
          <radialGradient
            id="paint0_radial_867_36444"
            cx="0"
            cy="0"
            r="1"
            gradientTransform="matrix(-142.32567 -35.7339 32.81076 -130.68302 287.495 142.318)"
            gradientUnits="userSpaceOnUse"
          >
            <stop stopColor="#FFA12F"></stop>
            <stop offset="1" stopColor="#FFA12F" stopOpacity="0"></stop>
          </radialGradient>
          <radialGradient
            id="paint1_radial_867_36444"
            cx="0"
            cy="0"
            r="1"
            gradientTransform="rotate(61.544 23.133 164.514) scale(128.013 166.012)"
            gradientUnits="userSpaceOnUse"
          >
            <stop stopColor="#FF2059"></stop>
            <stop offset="1" stopColor="#FF2059" stopOpacity="0.03"></stop>
          </radialGradient>
          <radialGradient
            id="paint2_radial_867_36444"
            cx="0"
            cy="0"
            r="1"
            gradientTransform="rotate(24.21 -182.858 112.95) scale(171.531 180.953)"
            gradientUnits="userSpaceOnUse"
          >
            <stop stopColor="#9E36F1"></stop>
            <stop offset="0.772" stopColor="#9E36F1" stopOpacity="0.05"></stop>
          </radialGradient>
          <radialGradient
            id="paint3_radial_867_36444"
            cx="0"
            cy="0"
            r="1"
            gradientTransform="matrix(-38.12229 116.7664 -104.7962 -34.21422 184.704 17.11)"
            gradientUnits="userSpaceOnUse"
          >
            <stop stopColor="#07C799"></stop>
            <stop offset="1" stopColor="#07C799" stopOpacity="0"></stop>
          </radialGradient>
        </defs>
      </svg>
      <Particles containerRef={trigger} styles={styles} />
    </div>
  );
}
